import React, { useState, useMemo, useRef, useCallback } from "react";
import { Segmented } from "antd";
import { useSelector } from "react-redux";
import { Swiper } from "antd-mobile";
import PeiChart from "@containers/facility/Overview/PieChart";
import "./SwitchablePieChart.css";

const SwitchablePieChart = ({ configs = [], defaultActiveKey }) => {
  const [activeKey, setActiveKey] = useState(
    defaultActiveKey || configs[0]?.key
  );
  const view = useSelector((state) => state.App.view);
  const isMobile = view === "MobileView";

  const segmentedOptions = useMemo(
    () => configs.map((c) => ({ label: c.label, value: c.key })),
    [configs]
  );

  const activeConfig = useMemo(
    () => configs.find((c) => c.key === activeKey) || configs[0],
    [configs, activeKey]
  );

  const activeIndex = useMemo(
    () => configs.findIndex((c) => c.key === activeKey),
    [configs, activeKey]
  );

  const swiperRef = useRef(null);

  const handleSegmentedChange = useCallback((val) => {
    setActiveKey(val);
    const idx = configs.findIndex((c) => c.key === val);
    if (swiperRef.current && idx >= 0) {
      swiperRef.current.swipeTo(idx);
    }
  }, [configs]);

  const handleSwiperChange = useCallback((idx) => {
    const config = configs[idx];
    if (config) {
      setActiveKey(config.key);
    }
  }, [configs]);

  const renderSegmented = () => (
    <div className="switchable-pie-header">
      <Segmented
        options={segmentedOptions}
        value={activeKey}
        onChange={handleSegmentedChange}
        size="small"
      />
    </div>
  );

  const nextConfig = configs[(activeIndex + 1) % configs.length];

  const renderMobileToggle = () => (
    <div className="switchable-pie-header">
      <button
        className="switchable-pie-toggle-btn"
        onClick={() => handleSegmentedChange(nextConfig.key)}
      >
        {nextConfig.label}
      </button>
    </div>
  );

  const renderChart = (config) => (
    <PeiChart
      data={config.data}
      height={200}
      colors={config.colors}
      title={config.title}
      handleChartClick={config.onClick}
    />
  );

  if (isMobile) {
    return (
      <div className="switchable-pie-mobile">
        {renderMobileToggle()}
        <div className="switchable-pie-swiper-wrap">
          <Swiper
            ref={swiperRef}
            loop={false}
            className="switchable-pie-swiper"
            defaultIndex={activeIndex >= 0 ? activeIndex : 0}
            onChange={handleSwiperChange}
            indicator={(total, current) => (
              <div className="switchable-pie-dots">
                {Array.from({ length: total }, (_, i) => (
                  <span
                    key={i}
                    className={`switchable-pie-dot${i === current ? " active" : ""}`}
                  />
                ))}
              </div>
            )}
          >
            {configs.map((config) => (
              <Swiper.Item key={config.key}>
                <div className="switchable-pie-swiper-item">
                  {renderChart(config)}
                </div>
              </Swiper.Item>
            ))}
          </Swiper>
        </div>
      </div>
    );
  }

  return (
    <div className="switchable-pie-wrapper">
      {renderSegmented()}
      <div className="switchable-pie-body">
        {activeConfig && renderChart(activeConfig)}
      </div>
    </div>
  );
};

export default SwitchablePieChart;
